import { Trans } from 'react-i18next';
import { t } from 'i18next';

import Link from '@mui/material/Link';
import SyncIcon from '@mui/icons-material/Sync';

import {
  ColumnProps,
  Page,
  Table,
  useFilterConfig,
  usePanelState,
  useTableQueryParams,
} from 'components';
import { FilterOptionModel, HealthAndSafetyModel } from 'models';
import { useWorkerListSearch } from 'features/workers';
import { useExportHealthAndSafetyList, useHealthAndSafetyList } from 'features/healthAndSafety';
import { useValueSetList } from 'features/valueSets';
import { getFieldDataAndAccessor } from 'utils/useFieldDataAndAccessor';
import { getValueSetValues } from 'utils/valueSetFunctions';
import { getPageName } from 'utils/format';
import { DATE_TIME_FORMAT_WITH_SECONDS, formatDate } from 'utils/dates';
import Yup from 'utils/yup';
import { openPhotoInNewTab } from 'utils/getPhotoLink';
import { RouteNames } from 'config/routeNames';
import { PhotoAccess } from 'config/photosConfig';
import LeoTextField from 'components/input-fields/leoTextField/LeoTextField';
import LeoDateInterval, {
  relativeDateTestFunction,
} from 'components/input-fields/leoDateInterval/LeoDateInterval';
import LeoSelectFieldMulti from 'components/input-fields/leoSelectField/LeoSelectFieldMulti';
import { getYesOrNoLabel } from 'pages/Leo/ReadingUnitDetails/ReadingUnitDetailsPage';

import { ReactComponent as Export } from 'assets/icons/Material/fileDownload.svg';

interface PhotoLinkProps {
  hs: HealthAndSafetyModel;
}

interface CoordinatesProps extends PhotoLinkProps {
  isLatitude: boolean;
}

const Coordinates = ({ hs, isLatitude }: CoordinatesProps) => (
  <Link
    variant="cellName"
    underline="none"
    fontWeight="bold"
    target="_blank"
    href={`https://maps.google.com?q=${hs.latitude},${hs.longitude}`}
  >
    {isLatitude ? hs.latitude : hs.longitude}
  </Link>
);

const PhotoLink = ({ hs }: PhotoLinkProps) => {
  const handleClick = () => openPhotoInNewTab(PhotoAccess.HEALTH_AND_SAFETY, hs.id.toString());

  return (
    <Link
      variant="cellName"
      underline="none"
      fontWeight="bold"
      sx={{ cursor: 'pointer' }}
      onClick={handleClick}
    >
      <Trans i18nKey="KOZOS_H_S_JELZESEK.VIEW_PHOTO" />
    </Link>
  );
};

const HealthAndSafetyPage = () => {
  const {
    onPageChange,
    onRowsPerPageChange,
    onSort,
    params,
    apiParams,
    exportParams,
    onFilter,
    onLoadQuery,
    onTableColumnVisibilityChange,
  } = useTableQueryParams();

  const {
    data: healthAndSafetyData,
    isLoading,
    isFetching,
    dataUpdatedAt,
  } = useHealthAndSafetyList(apiParams);
  const {
    isLoading: isExportLoading,
    isRefetching,
    refetch,
  } = useExportHealthAndSafetyList(exportParams);
  const { data: workersData } = useWorkerListSearch({ withDeleted: true });
  const { data: valueSet } = useValueSetList({ code: ['KT'] });
  const { data: workerData, accessor: workerAccessor } = getFieldDataAndAccessor(
    workersData,
    'sapId',
    'name',
    true
  );
  const { data: publicUtilityTypeData, accessor: publicUtilityTypeAccessor } =
    getFieldDataAndAccessor(getValueSetValues(valueSet, 'KT'));

  const headerActions = [
    {
      name: 'export',
      label: 'COMMON.EXPORT',
      icon: <Export fill="currentColor" />,
      onClick: () => refetch(),
      isLoading: isExportLoading || isRefetching,
      disabledIf: !exportParams?.filter,
    },
    {
      name: 'sync',
      label: 'LEO_H_S_JELZESEK.LEO_TRANSFER',
      icon: <SyncIcon fill="currentColor" />,
    },
  ];

  const filters: FilterOptionModel[] = [
    {
      headerTitle: 'KOZOS_H_S_JELZESEK.RECORDING_DATE',
      component: <LeoDateInterval name="recordingDate" />,
      name: 'recordingDate',
      panelFieldWidth: 12,
      panelFieldOffset: 12,
      divider: true,
    },
    {
      headerTitle: 'KOZOS_H_S_JELZESEK.SAP_ID',
      component: (
        <LeoSelectFieldMulti
          name="worker"
          data={workerData}
          accessor={workerAccessor}
          label="KOZOS_H_S_JELZESEK.SAP_ID"
          readOnly
        />
      ),
      name: 'worker',
      divider: true,
      panelFieldWidth: 12,
    },
    {
      headerTitle: 'KOZOS_H_S_JELZESEK.CITY',
      component: <LeoTextField name="city" label={<Trans i18nKey="KOZOS_H_S_JELZESEK.CITY" />} />,
      name: 'city',
      divider: true,
      panelFieldWidth: 12,
    },
    {
      headerTitle: 'COMMON.PUBLIC_UTILITY_TYPE',
      component: (
        <LeoSelectFieldMulti
          name="publicUtilityTypes"
          data={publicUtilityTypeData}
          accessor={publicUtilityTypeAccessor}
          label="COMMON.PUBLIC_UTILITY_TYPE"
          readOnly
        />
      ),
      name: 'publicUtilityTypes',
      panelFieldWidth: 12,
      abbrName: t('FILTERABBR.PUBLIC_UTILITY_TYPE'),
    },
  ];

  const healthAndSafetyColumns: ColumnProps<HealthAndSafetyModel>[] = [
    {
      key: 'recordingDate',
      header: <Trans i18nKey="KOZOS_H_S_JELZESEK.RECORDING_DATE" />,
      accessor: (hs) => formatDate(hs.recordingDate),
      field: 'recordingDate',
      sortable: true,
    },
    {
      key: 'worker.sapId',
      header: <Trans i18nKey="KOZOS_H_S_JELZESEK.SAP_ID" />,
      accessor: (hs) => hs.worker?.sapId,
      field: 'worker.sapId',
      sortable: true,
    },
    {
      key: 'city',
      header: <Trans i18nKey="KOZOS_H_S_JELZESEK.CITY" />,
      accessor: 'city',
      sortable: true,
    },
    {
      key: 'street',
      header: <Trans i18nKey="KOZOS_H_S_JELZESEK.STREET" />,
      accessor: 'street',
      sortable: true,
    },
    {
      key: 'streetNumber',
      header: <Trans i18nKey="KOZOS_H_S_JELZESEK.STREET_NUMBER" />,
      accessor: 'streetNumber',
      sortable: true,
    },
    {
      key: 'description',
      header: <Trans i18nKey="KOZOS_H_S_JELZESEK.DESCRIPTION" />,
      accessor: 'description',
      sortable: true,
    },
    {
      key: 'isReported',
      header: <Trans i18nKey="KOZOS_H_S_JELZESEK.IS_REPORTED" />,
      accessor: (hs) => getYesOrNoLabel(hs.isReported),
      field: 'isReported',
      sortable: true,
    },
    {
      key: 'latitude',
      header: <Trans i18nKey="KOZOS_H_S_JELZESEK.LATITUDE" />,
      accessor: (hs) => <Coordinates hs={hs} isLatitude={true} />,
      field: 'latitude',
      sortable: true,
    },
    {
      key: 'longitude',
      header: <Trans i18nKey="KOZOS_H_S_JELZESEK.LONGITUDE" />,
      accessor: (hs) => <Coordinates hs={hs} isLatitude={false} />,
      field: 'longitude',
      sortable: true,
    },
    {
      key: 'leoUpload.syncedAt',
      header: <Trans i18nKey="KOZOS_H_S_JELZESEK.TIMESTAMP" />,
      accessor: (hs) => formatDate(hs.leoUpload?.syncedAt, DATE_TIME_FORMAT_WITH_SECONDS),
      field: 'leoUpload.syncedAt',
      sortable: true,
    },
    {
      key: 'photo',
      header: <Trans i18nKey="KOZOS_H_S_JELZESEK.PHOTO_BUTTON" />,
      accessor: (hs) => <PhotoLink hs={hs} />,
      field: 'photo',
      sortable: false,
      align: 'right',
    },
  ];

  const { activePanel, closePanel, setPanel } = usePanelState();

  const validationSchema = Yup.object().shape({
    recordingDate: Yup.object()
      .nullable()
      .shape({
        from: Yup.mixed()
          .nullable()
          .test('isDate validation', t('ERRORS.INVALID_DATE'), relativeDateTestFunction),
        to: Yup.mixed()
          .nullable()
          .test('isDate validation', t('ERRORS.INVALID_DATE'), relativeDateTestFunction),
      }),
    badInterval: Yup.bool().oneOf([true], 'bad interval'),
  });

  const filterConfig = useFilterConfig({
    params,
    onFilter,
    filters,
    onLoad: onLoadQuery,
    isButton: true,
    pageName: getPageName(RouteNames.KOZOS_H_S_JELZESEK),
    validationSchema,
    isFetching,
  });

  const panels = [filterConfig];

  return (
    <Page
      title={<Trans i18nKey="KOZOS_H_S_JELZESEK.TITLE" />}
      inScroll
      panels={panels}
      activePanel={activePanel}
      closePanel={closePanel}
      setPanel={setPanel}
    >
      <Table
        list={healthAndSafetyData?.data || []}
        total={healthAndSafetyData?.meta.total}
        timestamp={dataUpdatedAt}
        loading={isLoading}
        params={params}
        onSort={onSort}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        columns={healthAndSafetyColumns}
        enableCheckbox={false}
        onTableColumnVisibilityChange={onTableColumnVisibilityChange}
        panels={panels}
        pageHeaderActions={headerActions}
        activePanel={activePanel}
        setPanel={setPanel}
        onLoadQuery={onLoadQuery}
        filterDetailsData={{ publicUtilityTypeAccessor }}
      />
    </Page>
  );
};

export default HealthAndSafetyPage;
